import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clapperboard, MessageSquare, FileText, Loader2, FolderClock, Trash2, Eye, Download, Cloud } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { openCvForPrint } from "@/lib/cvPrint";

export default function Projects() {
  const { t } = useT();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/projects");
      setProjects([]);
      toast.success(
        (t("projects_cleared_count") || "Cleared {count} projects").replace(
          "{count}",
          String(data?.deleted ?? 0)
        )
      );
    } catch {
      toast.error(t("err_generic"));
    } finally {
      setClearing(false);
      setConfirmClearAll(false);
    }
  };

  const downloadOne = async (id) => {
    setDownloadingId(id);
    try {
      const { data } = await api.get(`/projects/${id}`);
      // CV project → open as printable HTML in new tab (Blob URL)
      if (data?.kind === "cv") {
        const markdown = data?.content?.markdown;
        if (!markdown) { toast.error("CV content not found"); return; }
        const ok = openCvForPrint(markdown, data?.title || "CV");
        if (ok) toast.success("Opening CV for print/PDF…");
        else toast.error("Please allow pop-ups to download the CV");
        return;
      }
      if (!data?.video_url) {
        toast.error(t("project_no_video"));
        return;
      }
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_BACKEND_URL}${data.video_url}`;
      link.download = `lumioo-${(data.title || id).replace(/[^\w-]+/g, "_").slice(0, 60)}.mp4`;
      link.target = "_blank";
      link.click();
      toast.success(t("project_download_started"));
    } catch {
      toast.error(t("err_generic"));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success(t("project_deleted"));
    } catch {
      toast.error(t("err_generic"));
    } finally {
      setConfirmId(null);
    }
  };

  return (
    <div data-testid="projects-page">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("projects_eyebrow")}</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("projects_h1")}</h1>
      <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
        <p className="text-zinc-600">{t("projects_sub")}</p>
        {projects.length > 0 && !loading && (
          confirmClearAll ? (
            <div className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5" data-testid="projects-clear-confirm">
              <span className="text-xs font-semibold text-red-700">{t("projects_clear_confirm") || "Delete all projects?"}</span>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={clearing}
                className="text-xs font-bold px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                data-testid="projects-clear-yes"
              >
                {clearing ? "…" : (t("projects_clear_yes") || "Yes, delete all")}
              </button>
              <button
                type="button"
                onClick={() => setConfirmClearAll(false)}
                disabled={clearing}
                className="text-xs font-semibold text-zinc-600 hover:text-zinc-900"
                data-testid="projects-clear-cancel"
              >
                {t("cancel") || "Cancel"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmClearAll(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-red-600 px-3 py-1.5 rounded-md border border-zinc-200 hover:border-red-300 transition-colors"
              data-testid="projects-clear-all"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t("projects_clear_all") || "Clear all"}
            </button>
          )
        )}
      </div>

      <div className="mt-10 border border-zinc-200 bg-white">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="p-12 text-center" data-testid="projects-empty">
            <FolderClock className="w-10 h-10 mx-auto text-zinc-400" strokeWidth={1.2} />
            <p className="mt-3 text-sm text-zinc-500">{t("projects_empty")}</p>
            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
              <Link to="/app/chat" className="btn-outline text-sm font-semibold px-4 py-2 rounded-md">{t("try_chat")}</Link>
              <Link to="/app/video-producer" className="btn-accent text-sm font-semibold px-4 py-2 rounded-md">{t("try_video")}</Link>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200" data-testid="projects-list">
            {projects.map((p) => {
              const isCv = p.kind === "cv";
              return (
              <li key={p.id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`project-row-${p.id}`}>
                <Link to={`/app/projects/${p.id}`} className="flex items-center gap-3 min-w-0 flex-1 hover:underline">
                  {p.kind === "video" ? (
                    <Clapperboard className="w-4 h-4 text-blue-600 shrink-0" />
                  ) : isCv ? (
                    <FileText className="w-4 h-4 text-orange-600 shrink-0" />
                  ) : p.kind === "salesforce" ? (
                    <Cloud className="w-4 h-4 shrink-0" style={{ color: "#00A1E0" }} />
                  ) : p.kind === "trailhead" ? (
                    <span className="text-base leading-none shrink-0" title="Trailhead Solver">🥾</span>
                  ) : (
                    <MessageSquare className="w-4 h-4 text-blue-600 shrink-0" />
                  )}
                  <span className="font-medium text-zinc-900 truncate">{p.title}</span>
                </Link>
                <span className="text-xs text-zinc-500 shrink-0 hidden sm:inline">{new Date(p.created_at).toLocaleString()}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <Link to={`/app/projects/${p.id}`} data-testid={`project-view-${p.id}`}
                    className="p-2 hover:bg-zinc-100 rounded text-zinc-600 hover:text-blue-600" title={t("project_view")}>
                    <Eye className="w-4 h-4" />
                  </Link>
                  {(p.kind === "video" || isCv) && (
                    <button onClick={() => downloadOne(p.id)} disabled={downloadingId === p.id}
                      data-testid={`project-download-${p.id}`}
                      className="p-2 hover:bg-amber-50 rounded text-zinc-600 hover:text-orange-600 disabled:opacity-50" title={isCv ? "Download PDF" : t("project_download")}>
                      {downloadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </button>
                  )}
                  {confirmId === p.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(p.id)} data-testid={`project-delete-confirm-${p.id}`}
                        className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded hover:bg-red-700">
                        {t("project_delete_confirm")}
                      </button>
                      <button onClick={() => setConfirmId(null)} className="px-2 py-1 border border-zinc-300 text-xs font-medium rounded hover:bg-zinc-50">
                        {t("project_cancel")}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmId(p.id)} data-testid={`project-delete-${p.id}`}
                      className="p-2 hover:bg-red-50 rounded text-zinc-600 hover:text-red-600" title={t("project_delete")}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
